import { useState } from "react";
import { BackIcon, ClockIcon, ExternalIcon, OkIcon, PixIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import type { CollateralPlan, CollateralQuote, PaymentMethod } from "@/services/rto";
import { dateLabel, dayMonth, money, shortDow } from "@/utils/format";

// Rider picks the charge due date: today through today + 7 days (PM 2026-07-27).
// The backend re-clamps to [today, today+7], so this list is just the UI.
const buildDueOptions = (): string[] => {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i <= 7; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    out.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`,
    );
  }
  return out;
};

/* Collateral step. All amounts come from the backend quote (eligibility) and
   from the /collateral/link response — the LP renders, never computes.
   Variants: chooser (pick upfront/installments, then PIX or card), awaiting
   payment (PIX QR on-page, or hosted link in another tab; poll "Já paguei"),
   credit (surplus — no payment at all). */

export type PixData = { qr_code: string; qr_code_base64: string };

export type CollateralMode =
  | { kind: "chooser"; quote: CollateralQuote }
  | {
      kind: "awaiting";
      initPoint: string | null;
      pix: PixData | null;
      entryDue: number;
      plan: CollateralPlan;
      // YYYY-MM-DD the rider chose to pay by. Absent on resume (the status
      // read doesn't echo the doc's due date) — the "pay by" row hides then.
      dueDate?: string;
    }
  | { kind: "credit"; paid: number; target: number; surplus: number };

export function CollateralScreen({
  mode,
  pendingWarn,
  busy,
  onBack,
  onGenerateLink,
  onReopenLink,
  onCheckPaid,
  onCreditContinue,
}: {
  mode: CollateralMode;
  pendingWarn: boolean;
  busy: boolean;
  onBack: (() => void) | null;
  onGenerateLink: (plan: CollateralPlan, method: PaymentMethod, dueDate?: string) => void;
  onReopenLink: () => void;
  onCheckPaid: () => void;
  onCreditContinue: () => void;
}) {
  const [plan, setPlan] = useState<CollateralPlan | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<{ plan: CollateralPlan; method: PaymentMethod } | null>(
    null,
  );
  const dueOptions = buildDueOptions();

  const copyPixCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard denied — the code stays selectable below */
    }
  };

  if (mode.kind === "credit") {
    return (
      <section className="screen-enter" data-testid="screen-collateral-credit">
        <h2>Sua caução já está paga — e sobrou troco</h2>
        <div className="lpc-ok">
          <OkIcon />
          <span>
            Você pagou <strong>{money(mode.paid)}</strong> e a caução do Conquista é{" "}
            <strong>{money(mode.target)}</strong>. A diferença de{" "}
            <strong data-testid="credit-surplus">{money(mode.surplus)}</strong> volta pra você como
            desconto, dividida nas próximas 5 cobranças semanais.
          </span>
        </div>
        <button
          className="btn btn--primary"
          data-testid="credit-continue"
          onClick={onCreditContinue}
        >
          Continuar
        </button>
      </section>
    );
  }

  if (mode.kind === "awaiting") {
    return (
      <section className="screen-enter" data-testid="screen-collateral-awaiting">
        <h2>Caução: falta {money(mode.entryDue)}</h2>
        {mode.dueDate ? (
          <div className="lpc-math" data-testid="due-label" style={{ marginBottom: 14 }}>
            <div
              className="lpc-math__row"
              style={{ fontWeight: 800, color: "#fff", alignItems: "center" }}
            >
              <span>Pague até</span>
              <span>{dateLabel(mode.dueDate)}</span>
            </div>
          </div>
        ) : null}
        <div className="lpc-warn" data-testid="due-warn">
          <ClockIcon />
          <span>
            Se você não pagar até a data escolhida, a migração é cancelada e você precisa refazer o
            processo — a cobrança nunca fica vencida.
          </span>
        </div>
        {mode.pix ? (
          <div className="lpc-pix" data-testid="pix-block">
            <div className="lpc-pix__qr">
              <img
                src={`data:image/png;base64,${mode.pix.qr_code_base64}`}
                alt="QR Code PIX"
                data-testid="pix-qr"
              />
            </div>
            <div className="lpc-note">
              <PixIcon />
              <span>Escaneia o QR no app do seu banco, ou copia o código abaixo.</span>
            </div>
            <button
              className="btn btn--ghost lpc-pix__copy"
              data-testid="pix-copy"
              onClick={() => void copyPixCode(mode.pix!.qr_code)}
            >
              {copied ? "Copiado ✓" : "Copiar código PIX"}
            </button>
          </div>
        ) : (
          <>
            <div className="lpc-note">
              <ExternalIcon />
              <span>O pagamento abre em outra guia. Pagou? Volta aqui e confirma.</span>
            </div>
            {mode.initPoint ? (
              // Anchor no gesto do clique: o window.open pós-await é bloqueado
              // como popup em Safari/Chrome — este é o caminho garantido.
              <a
                className="btn btn--primary"
                data-testid="open-checkout"
                href={mode.initPoint}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir pagamento
              </a>
            ) : null}
            <div style={{ height: 11 }} />
          </>
        )}
        <button
          className={`btn ${mode.pix || !mode.initPoint ? "btn--primary" : "btn--ghost"}`}
          data-testid="check-paid"
          disabled={busy}
          onClick={onCheckPaid}
        >
          {busy ? "Verificando…" : "Já paguei"}
        </button>
        <div style={{ height: 11 }} />
        <button className="btn btn--ghost" data-testid="reopen-link" onClick={onReopenLink}>
          {mode.pix ? "Prefiro pagar com cartão" : "Gerar um link novo"}
        </button>
        {!mode.pix ? (
          <>
            <div style={{ height: 11 }} />
            <button
              className="btn btn--ghost"
              data-testid="switch-to-pix"
              disabled={busy}
              onClick={() => onGenerateLink(mode.plan, "PIX", mode.dueDate)}
            >
              Pagar com PIX
            </button>
          </>
        ) : null}
        {pendingWarn ? (
          <div className="lpc-warn" data-testid="pay-pending">
            <ClockIcon />
            <span>
              Ainda não achamos seu pagamento. Ele pode levar alguns minutos pra cair — tenta de
              novo já já.
            </span>
          </div>
        ) : null}
      </section>
    );
  }

  const quote = mode.quote;
  const inst = quote.installments;
  return (
    <section className="screen-enter" data-testid="screen-collateral">
      {onBack ? (
        <button className="lpc-back" data-testid="back-to-plans" onClick={onBack}>
          <BackIcon />
          Voltar
        </button>
      ) : null}
      <h2>Caução: você só paga a diferença</h2>
      {quote.paid > 0 ? (
        <div className="lpc-math">
          <div
            className="lpc-math__row"
            style={{ fontWeight: 800, color: "#fff", alignItems: "center" }}
          >
            <span>Você já pagou</span>
            <span className="lpc-math__paid" data-testid="paid-amount">
              − {money(quote.paid)}
            </span>
          </div>
        </div>
      ) : null}

      <button
        className={"lpc-select" + (plan === "UPFRONT" ? " is-selected" : "")}
        data-testid="plan-upfront"
        onClick={() => setPlan("UPFRONT")}
      >
        <span className="lpc-select__title">À vista</span>
        <span className="lpc-select__price">
          {money(quote.upfront.due)} <small>restantes agora</small>
        </span>
        <span className="lpc-select__desc">Pagou, acabou — nada somado na semanal.</span>
        <span className="lpc-select__micro">{money(quote.upfront.target)} no total</span>
      </button>
      <button
        className={"lpc-select" + (plan === "INSTALLMENTS" ? " is-selected" : "")}
        data-testid="plan-installments"
        onClick={() => setPlan("INSTALLMENTS")}
      >
        <span className="lpc-select__title">Parcelado na semanal</span>
        <span className="lpc-select__price">
          {money(inst.entry_due)} <small>restantes agora</small>
        </span>
        <span className="lpc-select__desc">
          + {inst.weekly_installments}× {money(inst.installment_value)} somados na sua cobrança
          semanal.
        </span>
        <span className="lpc-select__micro">
          {money(quote.paid + inst.entry_due + inst.weekly_installments * inst.installment_value)}{" "}
          no total
        </span>
      </button>

      {plan ? (
        <div data-testid="due-date-picker">
          <p style={{ fontWeight: 700, fontSize: 15, marginTop: 26 }}>
            Escolha o dia do pagamento{" "}
            <span style={{ color: "var(--g500)", fontWeight: 500 }}>(hoje ou até 7 dias)</span>
          </p>
          <div className="lpc-dates">
            {dueOptions.map((iso) => (
              <button
                key={iso}
                className={"lpc-date" + (dueDate === iso ? " is-selected" : "")}
                data-testid={`due-${iso}`}
                onClick={() => setDueDate(iso)}
              >
                <div className="lpc-date__dow">{shortDow(iso)}</div>
                <div className="lpc-date__num">{dayMonth(iso)}</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <button
        className="btn btn--primary"
        data-testid="generate-pix"
        disabled={!plan || !dueDate || busy}
        onClick={() => plan && dueDate && setConfirm({ plan, method: "PIX" })}
      >
        {busy ? "Gerando…" : "Pagar com PIX"}
      </button>
      <div style={{ height: 11 }} />
      <button
        className="btn btn--ghost"
        data-testid="generate-link"
        disabled={!plan || !dueDate || busy}
        onClick={() => plan && dueDate && setConfirm({ plan, method: "CHECKOUT" })}
      >
        Pagar com cartão
      </button>

      {/* Double opt-in (Jerry/Mario 2026-07-24): riders were generating the
          charge impulsively and regretting — confirm with the upfront amount
          "screamed" before anything is created. */}
      <Modal
        config={
          confirm
            ? {
                title:
                  confirm.method === "PIX"
                    ? "Confirmar geração do PIX?"
                    : "Confirmar geração do link?",
                body: (
                  <>
                    <div className="lpc-modal__paybox" data-testid="confirm-amount">
                      <span className="lpc-modal__paybox-eyebrow">Você paga agora</span>
                      <span className="lpc-modal__paybox-amount">
                        {money(confirm.plan === "UPFRONT" ? quote.upfront.due : inst.entry_due)}
                      </span>
                    </div>
                    {confirm.plan === "UPFRONT" ? (
                      <p>
                        Esse valor da caução será cobrado à vista. Confirmar e gerar{" "}
                        {confirm.method === "PIX" ? "a cobrança PIX" : "o link de pagamento"}?
                      </p>
                    ) : (
                      <p>
                        Além do valor cobrado agora, {inst.weekly_installments}×{" "}
                        {money(inst.installment_value)} já serão adicionadas às suas semanais.
                        Confirmar e gerar{" "}
                        {confirm.method === "PIX" ? "a cobrança PIX" : "o link de pagamento"}?
                      </p>
                    )}
                  </>
                ),
                confirmLabel:
                  confirm.method === "PIX" ? "Confirmar e gerar PIX" : "Confirmar e gerar link",
                cancelLabel: "Voltar",
                onConfirm: () => dueDate && onGenerateLink(confirm.plan, confirm.method, dueDate),
              }
            : null
        }
        onClose={() => setConfirm(null)}
      />
    </section>
  );
}
